import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import MetaTags from '@/components/meta/MetaTags';
import BulletinsHeader from '@/components/bulletins/BulletinsHeader';
import BulletinsTabs from '@/components/bulletins/BulletinsTabs';
import { useInfiniteRelics } from '@/hooks/use-infinite-relics';
import RelicsGrid from '@/components/bulletins/RelicsGrid';
import BulletinLightbox from '@/components/bulletins/BulletinLightbox';
import { LoadingFallback } from '@/components/common/LoadingFallback';
import { useIsMobile } from '@/hooks/use-mobile';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

const Relics: React.FC = () => {
  const isMobile = useIsMobile();

  // Measure content area to respect left navigation on desktop
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentBounds, setContentBounds] = useState<{
    top: number; left: number; width: number; height: number;
  } | null>(null);
  const updateContentBounds = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setContentBounds({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, []);
  useEffect(() => {
    updateContentBounds();
    window.addEventListener('resize', updateContentBounds);
    return () => window.removeEventListener('resize', updateContentBounds);
  }, [updateContentBounds, isMobile]);
  const navigate = useNavigate();
  const { bulletinId } = useParams();
  const queryClient = useQueryClient();

  const activeTab: 'pour-toi' | 'updates' = 'pour-toi';
  const activeCategory = 'relics';

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteRelics(activeTab);

  const relics = useMemo(() => data?.pages.flatMap(p => p.items) ?? [], [data]);
  const selected = useMemo(() => relics.find(r => r.id === bulletinId) || null, [relics, bulletinId]);

  const handleLike = async (id: string) => {
    queryClient.setQueryData(['infinite-relics', activeTab], (oldData: any) => {
      if (!oldData?.pages?.length) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map((page: any) => ({
          ...page,
          items: page.items.map((item: any) => item.id === id ? { ...item, like_count: (item.like_count || 0) + 1 } : item)
        }))
      };
    });
  };

  const handleDelete = () => {
    queryClient.invalidateQueries({ queryKey: ['infinite-relics', activeTab], exact: false });
  };

  const handleRelicClick = (id: string) => {
    navigate(`/relics/${id}`);
  };

  const handleClose = () => {
    navigate('/relics');
  };

  const handleTopTabChange = (value: string) => {
    if (value === 'pour-toi') {
      navigate('/relics');
    } else {
      navigate(`/bulletins/${value}`);
    }
  };
  return (
    <div>
      <MetaTags 
        title="Relics – Evergreen community best posts"
        description="Browse saved Relics: the community's most loved posts that never expire."
        image="/og-image.png"
        url={window.location.href}
      />

      <div ref={contentRef} className="container max-w-7xl mx-auto pt-25 px-4 py-[22px]">
        <motion.div 
          initial={{ y: -20, opacity: 0 }} 
          animate={{ y: 0, opacity: 1 }} 
          transition={{ delay: 0.2, duration: isMobile ? 0.2 : 0.3 }} 
          className="mx-0"
        >
          <BulletinsHeader activeTab={activeTab} activeCategory={activeCategory} queuedCount={0} />
        </motion.div>

        <motion.div 
          initial={{ y: -10, opacity: 0 }} 
          animate={{ y: 0, opacity: 1 }} 
          transition={{ delay: 0.3, duration: isMobile ? 0.1 : 0.2 }} 
          className="mb-6"
        >
          <BulletinsTabs activeTab={activeTab} onTabChange={() => {}} activeCategory={activeCategory} showTabs={true} />
        </motion.div>

        {isLoading && relics.length === 0 ? (
          <div className="flex justify-center my-12"><LoadingFallback /></div>
        ) : (
          <RelicsGrid 
            relics={relics}
            onLike={handleLike}
            onDelete={handleDelete}
            activeTab={activeTab}
            onLoadMore={() => fetchNextPage()}
            isLoading={isFetchingNextPage}
            hasNextPage={!!hasNextPage}
            onRelicClick={handleRelicClick}
            selectedRelicId={bulletinId}
          />
        )}

        <AnimatePresence>
          {selected && (
            <BulletinLightbox
              bulletins={relics}
              selectedId={selected.id}
              onNavigate={(id) => handleRelicClick(id)}
              onClose={handleClose}
              contentBounds={!isMobile ? contentBounds : null}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Relics;
